import Vue from "vue";
import Vuex from "vuex";
import shuffle from "lodash/shuffle";

import player from "./modules/player";
import modals from "./modules/modals";

Vue.use(Vuex);

const effects = {
  sum: function(power) {
    return this.power + power;
  },
  double: function(power) {
    return power * 2;
  }
};

const getTowers = () => [
  shuffle([
    {
      state: "init", // init, fight, bonus, win, lose
      character: "astroplayroom",
      power: 3,
      effect: effects.sum,
      step: null
    },
    {
      state: "init",
      character: "uncharted",
      power: 6,
      effect: effects.sum,
      step: null
    }
  ]),
  shuffle([
    {
      state: "init",
      character: "littlebigplanet",
      power: 0,
      effect: effects.double,
      step: null
    },
    {
      state: "init",
      character: "untildawn",
      power: 4,
      effect: effects.sum,
      step: null
    },
    {
      state: "init",
      character: "ratchetclanc",
      power: 10,
      effect: effects.sum,
      step: null
    }
  ]),
  shuffle([
    {
      state: "init",
      character: "godofwar",
      power: 6,
      effect: effects.sum,
      step: null
    },
    {
      state: "init",
      character: "ghostoftsusima",
      power: 15,
      effect: effects.sum
    },
    {
      state: "init",
      character: "allstars",
      power: 25,
      effect: effects.sum,
      step: null
    },
    {
      state: "init",
      character: "daysgone",
      power: 27,
      effect: effects.sum,
      step: null
    }
  ]),
  shuffle([
    {
      state: "init",
      character: "darksouls",
      power: 300,
      effect: effects.sum,
      step: null
    },
    {
      state: "init",
      character: "thelastofus",
      power: 40,
      effect: effects.sum,
      step: null
    },
    {
      state: "init",
      character: "concretegenie",
      power: 31,
      effect: effects.sum,
      step: null
    },
    {
      state: "init",
      character: "bloodborne",
      power: 150,
      effect: effects.sum,
      step: null
    }
  ])
];

const getLevels = () => [
  {
    state: "init", // init / start / finish
    towers: getTowers().slice(0, 1),
    check: level =>
      level.towers.every(tower => tower.every(floor => floor.state === "win"))
  },
  {
    state: "init",
    towers: getTowers(),
    check: level => {
      const lastTower = level.towers[level.towers.length - 1];
      const allantFloor = lastTower.find(
        floor => floor.character === "darksouls"
      );
      return allantFloor.state === "win";
    }
  },
  {
    state: "init",
    towers: getTowers(),
    check: level => {
      const lastTower = level.towers[level.towers.length - 1];
      const clickerFloor = lastTower.find(
        floor => floor.character === "thelastofus"
      );

      return clickerFloor.step === 4;
    }
  },
  {
    state: "init",
    towers: getTowers(),
    check: (_, player) => {
      return player.power >= 1000;
    }
  }
];

const getDefaultState = () => ({
  status: "init", // init, start
  isSoundOn: false,
  levels: getLevels(),
  levelId: 1,
  isMobile: false
});

export default new Vuex.Store({
  modules: { player, modals },
  state: getDefaultState(),
  getters: {
    level(state) {
      return state.levels[state.levelId];
    },
    secretFound(state) {
      return state.levels.some(level => {
        if (!level.towers[3]) return false;
        const allantFloor = level.towers[3].find(
          floor => floor.character === "darksouls"
        );
        return allantFloor.state === "win" && allantFloor.step <= 9;
      });
    }
  },
  mutations: {
    start(state, withSound) {
      state.status = "start";
      state.isSoundOn = withSound;
    },
    startLevel(state) {
      const level = state.levels[state.levelId];
      Vue.set(level, "state", "start");
    },
    finishLevel(state) {
      const level = state.levels[state.levelId];
      Vue.set(level, "state", "finish");
    },
    failLevel(state) {
      const level = state.levels[state.levelId];
      Vue.set(level, "state", "fail");
    },
    nextLevel(state) {
      state.levelId++;
    },
    updateFloorState(state, payload) {
      const level = state.levels[state.levelId];
      const tower = level.towers[payload.towerId];
      const floor = tower[payload.floorId];

      const step = level.towers.reduce((acc, tower) => {
        return acc + tower.filter(floor => floor.state === "win").length;
      }, 0);

      Vue.set(floor, "step", step);
      Vue.set(floor, "state", payload.state);
    },
    toggleSound(state) {
      state.isSoundOn = !state.isSoundOn;
    },
    setMobile(state, { mobile }) {
      state.isMobile = mobile;
    }
  },
  actions: {
    restart({ state }) {
      Object.assign(state, {
        ...getDefaultState(),
        player: player.state(),
        modals: modals.state(),
        levelId: 1,
        status: "start",
        isSoundOn: state.isSoundOn,
        isMobile: state.isMobile
      });

      document.querySelector("#app").scroll(0, 0);
    },
    restartLevel({ state }, levelId) {
      const allantFloor = state.levels[1].towers[3].find(
        floor => floor.character === "darksouls"
      );

      let secretAlreadyFound = false;
      if (allantFloor.state === "win" && allantFloor.step <= 9) {
        secretAlreadyFound = true;
      }

      let levelsList = getLevels();
      levelsList.forEach((level, index) => {
        if (index !== 0 && index < levelId) Vue.set(level, "state", "finish");
      });

      if (secretAlreadyFound) {
        const allantFloorNew = levelsList[1].towers[3].find(
          floor => floor.character === "darksouls"
        );
        Vue.set(allantFloorNew, "state", "win");
        Vue.set(allantFloorNew, "step", 9);
      }

      Object.assign(state, {
        ...getDefaultState(),
        player: player.state(),
        modals: modals.state(),
        levelId: levelId,
        levels: levelsList,
        status: "start",
        isSoundOn: state.isSoundOn,
        isMobile: state.isMobile
      });

      document.querySelector("#app").scroll(0, 0);
    },
    next({ state, commit }) {
      Object.assign(state.player, player.state());

      if (state.modals.levelFinish) {
        commit("toggleModal", "levelFinish");
      }
      commit("nextLevel");
      if (!state.modals.levelStart) {
        setTimeout(() => commit("toggleModal", "levelStart"), 1000);
      }

      document.querySelector("#app").scroll(0, 0);
    }
  }
});
